import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TabContext from "@mui/lab/TabContext";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Icon } from "@iconify/react";
import Layout from "../../../../components/Layout";
import Button from "../../../../components/Button";
import BreadCrumb from "../../../../components/BreadCrumb";
import useStyles from "../../styles";
import { httprequest } from "../../../../data/api";
import { toast } from "react-toastify";
import MaterialPreviewModal from "../../components/MaterialPreviewModal";
import { formatFileSize, getFileMeta } from "../../employer/utils/uploadFile";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const SYLLABUS_ENDPOINT = "/api/v1/syllabus";
const SUBJECTS_ENDPOINT = "/api/v1/subjects";

const getProgressMeta = (progress) => {
  if (progress === "COMPLETED") return { label: "Completed", background: "#12B76A", color: "#fff", pillClass: "progressCompleted" };
  if (progress && progress !== "NOT_STARTED") return { label: "In Progress", background: "#155EEF", color: "#fff", pillClass: "progressInProgress" };

  return { label: "Not Started", background: "#F2F4F7", color: "#475467", pillClass: "progressNotStarted" };
};

const SyllabusDetails = () => {
  const { subjectId, syllabusId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();

  const [subjectName, setSubjectName] = useState("");
  const [syllabus, setSyllabus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState("syllabus");
  const [materials, setMaterials] = useState([]);
  const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [previewMaterial, setPreviewMaterial] = useState(null);

  const getSubject = (subId) => {
    if (!subId) return;
    httprequest.get(`${SUBJECTS_ENDPOINT}/${subId}`)
      .then((res) => setSubjectName(res?.data?.data?.name))
      .catch(() => {});
  };

  const resolveSyllabus = () => {
    setIsLoading(true);
    if (!syllabusId) {
      setSyllabus(null);
      setIsLoading(false);
      getSubject(subjectId);

      return;
    }
    httprequest.get(`${SYLLABUS_ENDPOINT}/${syllabusId}`)
      .then((detail) => {
        const data = detail?.data?.data;
        setSyllabus(data);
        getSubject(data?.subject_id || subjectId);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setSyllabus(null);
        toast.error(err?.response?.data?.message || "Failed to load syllabus details");
      });
  };

  const getMaterials = () => {
    if (!syllabus?.id) return;
    setIsMaterialsLoading(true);
    httprequest.get(`${SYLLABUS_ENDPOINT}/${syllabus.id}/materials`)
      .then((res) => {
        setMaterials(res?.data?.data || []);
        setIsMaterialsLoading(false);
      })
      .catch(() => {
        setIsMaterialsLoading(false);
      });
  };

  useEffect(() => {
    if (subjectId) {
      resolveSyllabus();
    }
  }, [subjectId]);

  useEffect(() => {
    if (tab === "materials" && syllabus?.id) getMaterials();
  }, [tab, syllabus?.id]);

  const toggleWeek = (weekId) => {
    setExpandedWeeks((prev) => ({ ...prev, [weekId]: !prev[weekId] }));
  };

  const handleDownloadSyllabus = () => {
    if (!syllabus) return;
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    doc.text(syllabus.title || "Syllabus", 14, 15);
    const tableData = [];
    syllabus.weeks?.forEach((week) => {
      if (week.topics && week.topics.length > 0) {
        week.topics.forEach((topic) => {
          tableData.push([`Week ${week.week}`, topic.title]);
        });
      } else {
        tableData.push([`Week ${week.week}`, "No topics"]);
      }
    });
    autoTable(doc, {
      head: [["Week", "Topics"]],
      body: tableData,
      startY: 25
    });
    doc.save("Syllabus.pdf");
  };

  return (
    <Layout
      children={
        <>
          <BreadCrumb
            link_one={`/class-activities/syllabus/${subjectId}`}
            link_one_name="Syllabus Details"
            active="Syllabus Content"
          />

          {isLoading ? (
            <div style={{ marginTop: "40px", textAlign: "center" }}>Loading...</div>
          ) : !syllabus ? (
            <div className={classes.emptyStateContainer} style={{ marginTop: "40px" }}>
              <div className={classes.emptyStateIcon}><Icon icon="mdi:book-open-blank-variant" /></div>
              <h3 className={classes.emptyStateTitle}>No syllabus available</h3>
              <p className={classes.emptyStateSubtitle}>
                The syllabus for this subject hasn't been published yet. Check back later to view the course
                outline, topics, and learning plan.
              </p>
            </div>
          ) : (
            <div style={{ marginTop: "20px" }}>
              <div className={`${classes.editorHeader} white_bg`}>
                <div style={{ display: "flex", gap: 12, width: "100%", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <div className={classes.weekBadge} style={{ width: 44, height: 44, borderRadius: 12, fontSize: 15 }}>
                      {subjectName?.slice(0, 2)?.toUpperCase() || "SY"}
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <h4 style={{ margin: 0 }}>{subjectName} {syllabus?.session?.name} - {syllabus?.term?.name}</h4>
                      <div className={classes.editorHeaderInfo} style={{ marginTop: 10 }}>
                        <span className={classes.metaChip}>
                          <Icon icon="mdi:calendar-blank-outline" /> {syllabus?.session?.name} Session
                        </span>
                        <span className={classes.metaChip}>{syllabus?.term?.name}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button
                      buttonName="Download Syllabus"
                      variant="primaryoutline"
                      color="btnprimary"
                      buttonIcon={<Icon icon="mdi:download-outline" style={{ marginRight: 6 }} />}
                      onClick={handleDownloadSyllabus}
                    />
                  </div>
                </div>
              </div>

              <TabContext value={tab}>
                <div className="tablist-container" style={{ marginTop: "20px" }}>
                  <Tabs className="overide-tablist" onChange={(e, val) => setTab(val)}>
                    <Tab label="Syllabus" value="syllabus" />
                    <Tab label="Materials" value="materials" />
                  </Tabs>
                </div>

                <TabPanel value="syllabus" className="tabpanel-overide">
                  <div className="white_bg" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {(syllabus?.weeks || []).length === 0 ? (
                      <div className={classes.emptyStateContainer} style={{ minHeight: "300px" }}>
                        <div className={classes.emptyStateIcon}><Icon icon="mdi:format-list-bulleted" /></div>
                        <h3 className={classes.emptyStateTitle}>No weeks added</h3>
                        <p className={classes.emptyStateSubtitle}>No syllabus topics have been scheduled yet.</p>
                      </div>
                    ) : (
                      syllabus?.weeks?.map((week) => {
                        const isExpanded = !!expandedWeeks[week.id];
                        const meta = getProgressMeta(week.progress);

                        return (
                          <div key={week.id} style={{ border: "1px solid #EAECF0", borderRadius: 10, marginBottom: 16, overflow: "hidden", background: "white" }}>
                            <div
                              style={{ padding: "14px 16px", display: "flex", alignItems: "center", cursor: "pointer", gap: 12 }}
                              onClick={() => toggleWeek(week.id)}
                            >
                              <span className={classes.weekBadge} style={{ background: meta.background, color: meta.color, flexShrink: 0 }}>
                                W{week.week}
                              </span>
                              <div style={{ flex: 1, textAlign: "left" }}>
                                <h5 style={{ margin: 0, fontSize: 16, color: "#101828" }}>{week.title}</h5>
                                <p style={{ margin: 0, fontSize: 13, color: "#667085" }}>Week {week.week}, {week?.topics?.length || 0} Topic{(week?.topics?.length === 1) ? "" : "s"}</p>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <span className={classes.progressBadge} style={{ background: meta.pillClass === "progressCompleted" ? "#ECFDF3" : meta.pillClass === "progressInProgress" ? "#EFF8FF" : "#F2F4F7", color: meta.pillClass === "progressCompleted" ? "#027A48" : meta.pillClass === "progressInProgress" ? "#175CD3" : "#475467" }}>
                                  {meta.label}
                                </span>
                                <Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} style={{ color: "#667085", fontSize: 20 }} />
                              </div>
                            </div>

                            {isExpanded && week.topics && week.topics.length > 0 && (
                              <div style={{ padding: "0 16px 20px 72px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12, textAlign: "left" }}>
                                {week.topics.map((topic) => (
                                  <div
                                    key={topic.id}
                                    style={{ cursor: "pointer", color: "#155EEF", fontSize: 14, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3, textAlign: "left" }}
                                    onClick={() => navigate(`/class-activities/student/syllabus/${subjectId}/${syllabus?.id}/topics/${topic?.id}`)}
                                  >
                                    {topic.title}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </TabPanel>

                <TabPanel value="materials" className="tabpanel-overide">
                  {materials.length === 0 && !isMaterialsLoading ? (
                    <div className={classes.emptyStateContainer} style={{ minHeight: "300px" }}>
                      <div className={classes.emptyStateIcon}><Icon icon="mdi:book-open-blank-variant" /></div>
                      <h3 className={classes.emptyStateTitle}>No class materials yet</h3>
                      <p className={classes.emptyStateSubtitle}>Your teacher hasn't shared any learning materials for this subject yet. Resources will appear here as they're added.</p>
                    </div>
                  ) : (
                    <div className={classes.materialsGrid}>
                      {materials.map((material) => {
                        const meta = getFileMeta(material?.file_name);

                        return (
                          <div key={material?.id} className={classes.materialCard} onClick={() => setPreviewMaterial(material)}>
                            <div className={classes.materialIcon} style={{ background: meta.bg, color: meta.color }}>
                              <Icon icon={meta.icon} />
                            </div>
                            <div style={{ minWidth: 0, textAlign: "left" }}>
                              <p className={classes.materialTitle}>{material?.title}</p>
                              <p className={classes.materialMeta}>{meta.label} . {formatFileSize(material?.file_size)}</p>
                            </div>
                            <div className={classes.materialActions}>
                              <Icon icon="mdi:download-outline" onClick={(e) => {
                                e.stopPropagation();
                                window.open(material?.file_url, "_blank");
                              }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabPanel>
              </TabContext>
            </div>
          )}

          <MaterialPreviewModal
            isOpen={!!previewMaterial}
            closeModal={() => setPreviewMaterial(null)}
            material={previewMaterial}
          />
        </>
      }
    />
  );
};

export default SyllabusDetails;
